import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";

const R = {
  en: { title: "Forgot password", hint: "Enter your email — we'll send you a secure link valid for 1 hour.", email: "Email", send: "Send reset link", sentTitle: "Check your inbox", sent: "If an account exists for this address, a reset link is on its way.", back: "← Back to login" },
  fr: { title: "Mot de passe oublié", hint: "Saisis ton email — on t'envoie un lien sécurisé valable 1 heure.", email: "Email", send: "Envoyer le lien", sentTitle: "Regarde ta boîte mail", sent: "Si un compte existe pour cette adresse, un lien de réinitialisation est en route.", back: "← Retour à la connexion" },
};

export default function ForgotPassword() {
  const { lang } = useLang();
  const r = R[lang] || R.en;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10" data-testid="forgot-page">
      <div className="w-full max-w-md rounded-2xl border border-[#1E2638] bg-[#121721] p-8">
        <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">DLSPORT · SÉCURITÉ</div>
        {sent ? (
          <div className="mt-3" data-testid="forgot-sent">
            <MailCheck className="w-10 h-10 text-[#00F076]" />
            <h1 className="font-display font-black uppercase text-3xl mt-3">{r.sentTitle}</h1>
            <p className="text-sm text-slate-400 mt-2">{r.sent}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <h1 className="font-display font-black uppercase text-3xl" data-testid="forgot-title">{r.title}</h1>
            <p className="text-sm text-slate-400">{r.hint}</p>
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{r.email}</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">{loading ? "…" : r.send}</Button>
          </form>
        )}
        <div className="mt-6 text-center">
          <Link to="/auth?mode=login" className="text-sm text-slate-400 hover:text-white" data-testid="forgot-back">{r.back}</Link>
        </div>
      </div>
    </div>
  );
}
